import React, { useState, useEffect } from 'react';
import {
  FaCalendarAlt,
  FaClock,
  FaEdit,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPlus,
  FaSearch,
  FaTrash,
  FaUserTie,
  FaUsers
} from 'react-icons/fa';
import { batchAPI, teacherAPI, getImageUrl } from '../services/api';
import Modal from '../components/Modal';
import './BatchesPage.css';

function BatchesPage({
  readOnly = false,
  title = 'Batch Management',
  subtitle = '',
  onEnroll = null,
  enrolledBatchIds = [],
  enrollingBatchId = null,
  onOpenPayments = null
}) {
  const [batches, setBatches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [paymentTiming, setPaymentTiming] = useState('Later');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    BatchName: '',
    TeacherID: '',
    AboutText: '',
    DaysOfWeek: '',
    StartTime: '',
    EndTime: '',
    MaxCapacity: '',
    DurationValue: '1',
    DurationUnit: 'Month',
    FeeAmount: '',
    LevelTag: '',
    Location: '',
    Highlights: '',
    IsActive: true,
    ImagePath: ''
  });

  useEffect(() => {
    fetchBatches();
    fetchTeachers();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await batchAPI.getAll();
      setBatches(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch batches: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await teacherAPI.getAll();
      setTeachers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch teachers:', err);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredBatches = batches.filter((batch) =>
    batch.BatchName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenModal = (batch = null) => {
    if (batch) {
      setEditingId(batch.BatchID);
      setFormData({
        BatchName: batch.BatchName || '',
        TeacherID: batch.TeacherID || '',
        AboutText: batch.AboutText || '',
        DaysOfWeek: batch.DaysOfWeek || '',
        StartTime: batch.StartTime || '',
        EndTime: batch.EndTime || '',
        MaxCapacity: batch.MaxCapacity || '',
        DurationValue: batch.DurationValue || '1',
        DurationUnit: batch.DurationUnit || 'Month',
        FeeAmount: batch.FeeAmount || '',
        LevelTag: batch.LevelTag || '',
        Location: batch.Location || '',
        Highlights: batch.Highlights || '',
        IsActive: batch.IsActive !== false,
        ImagePath: batch.ImagePath || ''
      });
    } else {
      setEditingId(null);
      setFormData({
        BatchName: '',
        TeacherID: '',
        AboutText: '',
        DaysOfWeek: '',
        StartTime: '',
        EndTime: '',
        MaxCapacity: '',
        DurationValue: '1',
        DurationUnit: 'Month',
        FeeAmount: '',
        LevelTag: '',
        Location: '',
        Highlights: '',
        IsActive: true,
        ImagePath: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleOpenEnrollmentPrompt = (batch) => {
    setSelectedBatch(batch);
    setPaymentTiming('Later');
  };

  const handleCloseEnrollmentPrompt = () => {
    setSelectedBatch(null);
    setPaymentTiming('Later');
  };

  const handleInputChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    if (type === 'file' && files.length > 0) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key === 'ImagePath') {
          if (formData[key] instanceof File) {
            data.append(key, formData[key]);
          }
        } else {
          data.append(key, formData[key]);
        }
      });

      if (editingId) {
        await batchAPI.update(editingId, data);
      } else {
        await batchAPI.create(data);
      }
      await fetchBatches();
      handleCloseModal();
      setError('');
    } catch (err) {
      setError('Failed to save batch: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this batch?')) {
      try {
        await batchAPI.delete(id);
        fetchBatches();
        setError('');
      } catch (err) {
        setError('Failed to delete batch: ' + err.message);
      }
    }
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find((t) => String(t.TeacherID) === String(teacherId));
    return teacher ? `${teacher.FirstName} ${teacher.LastName}`.trim() : 'N/A';
  };

  const renderBatchImage = (batch) => {
    if (batch.ImagePath) {
      return (
        <img
          src={getImageUrl(batch.ImagePath)}
          alt={batch.BatchName}
          className="student-card-image"
        />
      );
    }

    return (
      <div className="student-card-image student-card-fallback batch-fallback">
        <span>{batch.LevelTag || 'Batch'}</span>
        <strong>{batch.BatchName}</strong>
      </div>
    );
  };

  const renderBatchHighlights = (highlights) =>
    String(highlights || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  const handleConfirmEnrollment = async () => {
    if (!selectedBatch || !onEnroll) {
      return;
    }

    const result = await onEnroll(selectedBatch, { paymentTiming });
    if (paymentTiming === 'Now' && result?.InvoiceID) {
      onOpenPayments?.(result.InvoiceID);
    }
    handleCloseEnrollmentPrompt();
  };

  return (
    <div className="batches-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">{title}</h1>
            {subtitle && <p className="page-subtitle">{subtitle}</p>}
          </div>
          {!readOnly && (
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <FaPlus /> Add Batch
            </button>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search batches..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        {loading ? (
          <div className="loading">Loading batches...</div>
        ) : filteredBatches.length === 0 ? (
          <div className="no-data">No batches found</div>
        ) : readOnly ? (
          <div className="student-card-grid">
            {filteredBatches.map((batch) => {
              const isEnrolled = enrolledBatchIds.some((id) => String(id) === String(batch.BatchID));
              const highlightItems = renderBatchHighlights(batch.Highlights);

              return (
                <article key={batch.BatchID} className="student-detail-card batch-card-student">
                  <div className="batch-card-media">
                    {renderBatchImage(batch)}
                    <div className="batch-card-overlay">
                      <span className="student-card-chip">{batch.LevelTag || 'Open Level'}</span>
                      <span className="student-card-chip subtle">{`Rs ${Number(batch.FeeAmount || 0).toFixed(2)}`}</span>
                    </div>
                    <div className="batch-card-hero-copy">
                      <p>{`${batch.DurationValue || 1} ${batch.DurationUnit || 'Month'} plan`}</p>
                      <strong>{batch.BatchName}</strong>
                    </div>
                  </div>
                  <div className="student-card-body">
                    <p className="student-card-description">
                      {batch.AboutText || 'Structured skating training with coaching, schedule support, and measurable progress.'}
                    </p>
                    <div className="batch-card-detail-grid">
                      <span className="batch-detail-pill"><FaUserTie /> {getTeacherName(batch.TeacherID)}</span>
                      <span className="batch-detail-pill"><FaCalendarAlt /> {batch.DaysOfWeek || 'Schedule shared by coach'}</span>
                      <span className="batch-detail-pill"><FaClock /> {`${batch.StartTime || '--'} - ${batch.EndTime || '--'}`}</span>
                      <span className="batch-detail-pill"><FaMapMarkerAlt /> {batch.Location || 'Club venue'}</span>
                      <span className="batch-detail-pill"><FaMoneyBillWave /> {`${batch.DurationValue || 1} ${batch.DurationUnit || 'Month'} plan`}</span>
                      <span className="batch-detail-pill"><FaUsers /> Capacity: {batch.MaxCapacity || 'N/A'}</span>
                    </div>
                    {highlightItems.length > 0 && (
                      <div className="student-card-highlights">
                        {highlightItems.map((item) => (
                          <span key={item} className="student-card-chip outline">{item}</span>
                        ))}
                      </div>
                    )}
                    {isEnrolled ? (
                      <span className="status-badge active student-card-status">Enrolled</span>
                    ) : (
                      <button
                        className="btn btn-success student-card-action"
                        onClick={() => handleOpenEnrollmentPrompt(batch)}
                        disabled={enrollingBatchId === batch.BatchID}
                      >
                        {enrollingBatchId === batch.BatchID ? 'Enrolling...' : 'Enroll In Batch'}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>Batch Name</th>
                  <th>Subscription</th>
                  <th>Teacher</th>
                  <th>Days</th>
                  <th>Time</th>
                  <th>Capacity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBatches.map((batch) => (
                  <tr key={batch.BatchID}>
                    <td data-label="Batch Name">
                      <strong>{batch.BatchName}</strong>
                      <div>{batch.LevelTag || 'Open level'}</div>
                    </td>
                    <td data-label="Subscription">{`Rs ${Number(batch.FeeAmount || 0).toFixed(2)} / ${batch.DurationValue || 1} ${batch.DurationUnit || 'Month'}`}</td>
                    <td data-label="Teacher">{getTeacherName(batch.TeacherID)}</td>
                    <td data-label="Days">{batch.DaysOfWeek || 'N/A'}</td>
                    <td data-label="Time">
                      {batch.StartTime} - {batch.EndTime}
                    </td>
                    <td data-label="Capacity">{batch.MaxCapacity}</td>
                    <td data-label="Actions" className="action-buttons">
                      <button
                        className="btn btn-small btn-primary"
                        onClick={() => handleOpenModal(batch)}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(batch.BatchID)}
                      >
                        <FaTrash /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!readOnly && (
        <Modal
          isOpen={isModalOpen}
          title={editingId ? 'Edit Batch' : 'Add New Batch'}
          onClose={handleCloseModal}
        >
          <form onSubmit={handleSubmit} className="batch-form">
            <div className="form-group">
              <label>Batch Name *</label>
              <input
                type="text"
                name="BatchName"
                value={formData.BatchName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Teacher *</label>
              <select
                name="TeacherID"
                value={formData.TeacherID}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Teacher</option>
                {teachers.map((teacher) => (
                  <option key={teacher.TeacherID} value={teacher.TeacherID}>
                    {teacher.FirstName} {teacher.LastName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>About Batch</label>
              <textarea
                name="AboutText"
                value={formData.AboutText}
                onChange={handleInputChange}
                rows="3"
                placeholder="Describe who this batch is for and what students will learn"
              />
            </div>

            <div className="form-group">
              <label>Days of Week</label>
              <input
                type="text"
                name="DaysOfWeek"
                value={formData.DaysOfWeek}
                onChange={handleInputChange}
                placeholder="e.g., Monday, Wednesday, Friday"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="time"
                  name="StartTime"
                  value={formData.StartTime}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input
                  type="time"
                  name="EndTime"
                  value={formData.EndTime}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Duration</label>
                <input
                  type="number"
                  name="DurationValue"
                  value={formData.DurationValue}
                  onChange={handleInputChange}
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Duration Unit</label>
                <select
                  name="DurationUnit"
                  value={formData.DurationUnit}
                  onChange={handleInputChange}
                >
                  <option value="Week">Week</option>
                  <option value="Month">Month</option>
                  <option value="Year">Year</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Subscription Fee</label>
                <input
                  type="number"
                  name="FeeAmount"
                  value={formData.FeeAmount}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Level Tag</label>
                <input
                  type="text"
                  name="LevelTag"
                  value={formData.LevelTag}
                  onChange={handleInputChange}
                  placeholder="Beginner / Intermediate / Race Team"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="Location"
                value={formData.Location}
                onChange={handleInputChange}
                placeholder="Training venue or rink"
              />
            </div>

            <div className="form-group">
              <label>Highlights</label>
              <textarea
                name="Highlights"
                value={formData.Highlights}
                onChange={handleInputChange}
                rows="3"
                placeholder="Comma separated benefits like race drills, endurance, flexibility"
              />
            </div>

            <div className="form-group">
              <label>Batch Image</label>
              <input
                type="file"
                name="ImagePath"
                onChange={handleInputChange}
                accept="image/*"
              />
              <p className="form-help">Use a JPG or PNG image, recommended around 1200 x 900 px, so batch cards stay crisp across desktop and mobile.</p>
              {formData.ImagePath && (
                <div className="upload-preview">
                  <img
                    src={formData.ImagePath instanceof File ? URL.createObjectURL(formData.ImagePath) : getImageUrl(formData.ImagePath)}
                    alt="Batch"
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Max Capacity</label>
              <input
                type="number"
                name="MaxCapacity"
                value={formData.MaxCapacity}
                onChange={handleInputChange}
                min="1"
              />
            </div>

            <label className="sdb-checkbox">
              <input
                type="checkbox"
                name="IsActive"
                checked={Boolean(formData.IsActive)}
                onChange={handleInputChange}
              />
              Batch is active and open for enrollment
            </label>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update Batch' : 'Add Batch'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {readOnly && (
        <Modal
          isOpen={selectedBatch !== null}
          title={selectedBatch ? `Enroll in ${selectedBatch.BatchName}` : 'Choose Payment Option'}
          onClose={handleCloseEnrollmentPrompt}
        >
          <div className="enrollment-choice-copy">
            <p>Choose whether the student wants to complete the fee now or keep the invoice pending for later payment.</p>
          </div>

          <div className="enrollment-choice-grid">
            <button
              type="button"
              className={`enrollment-choice-card ${paymentTiming === 'Now' ? 'active' : ''}`}
              onClick={() => setPaymentTiming('Now')}
            >
              <strong>Payment Now</strong>
              <span>Create enrollment and open Razorpay payment from the student payments section.</span>
            </button>
            <button
              type="button"
              className={`enrollment-choice-card ${paymentTiming === 'Later' ? 'active' : ''}`}
              onClick={() => setPaymentTiming('Later')}
            >
              <strong>Pay Later</strong>
              <span>Create enrollment and keep the invoice pending so the coach can track it.</span>
            </button>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirmEnrollment}
              disabled={!selectedBatch || enrollingBatchId === selectedBatch?.BatchID}
            >
              {enrollingBatchId === selectedBatch?.BatchID ? 'Enrolling...' : 'Confirm Enrollment'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseEnrollmentPrompt}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default BatchesPage;
